import { Directive, Input, OnChanges, SimpleChanges } from "@angular/core";
import { DateUtils } from "@flipto/shared/src/lib/utils/common/time/date-utils";
import dayjs from 'dayjs/esm/index';
import isBetween from 'dayjs/esm/plugin/isBetween';

dayjs.extend(isBetween);

@Directive()
export abstract class CalendarDayBase<DateRefType> implements OnChanges {
    date: Date;
    @Input() dateRef: DateRefType;
    @Input() dateType: any;
    @Input() disabled: boolean;
    @Input() startDate: Date;
    @Input() endDate: Date;
    @Input() hoveredDate: Date;
    @Input() hoveredMonth: Date;
    @Input() focused: boolean;
    @Input() selectedMonths: Date[];

    available: boolean;
    selected: boolean;
    static: boolean;
    range: boolean;
    rangeStart: boolean;
    rangeEnd: boolean;
    monthRange: boolean
    monthRangeStart: boolean
    monthRangeEnd: boolean
    suggested: boolean;
    inactive: boolean;
    hovered: boolean;

    hoveredRangeStart: boolean;
    hoveredRange: boolean;
    hoveredRangeEnd: boolean;

    constructor() {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.dateRef) {
            this.setDate();
        }
    }

    setDate(): void{
        this.date = this.dateRefToDate(this.dateRef);
    }

    abstract dateRefToDate(dateRef: DateRefType): Date;


    // Base Flags
    setBaseFlags() {
        this.available = this.isAvailable();
        this.selected = this.isSelected();
        this.inactive = this.isInactive();
        this.suggested = this.isSuggested();
        this.static = this.isStatic();
    }

    isAvailable() {
        if (this.disabled) {
            return false;
        }

        return true;
    }

    isSelected() {
        return !this.disabled &&
            (this.startDate && DateUtils.isSame(this.startDate, this.date) ||
                (this.endDate && DateUtils.isSame(this.endDate, this.date)));
    }

    // Range Flags
    setRangeFlags() {
        this.hovered = this.isSame(this.hoveredDate)
        this.rangeStart = this.isRangeStart();
        this.range = this.isRange();
        this.rangeEnd = this.isRangeEnd();
    }

    setMonthRangeFlags() {
        this.monthRangeStart = this.isMonthRangeStart();
        this.monthRange = this.isMonthRange();
        this.monthRangeEnd = this.isMonthRangeEnd();
    }

    setHoveredRangeFlags() {
        this.hoveredRangeStart = this.isMonthRangeStart() && dayjs(this.date).isBetween(dayjs(this.hoveredDate), dayjs(this.selectedMonths[this.selectedMonths.length - 1]), 'month', '[]') && !this.isMonthRange();
        this.hoveredRange = this.hoveredDate && dayjs(this.date).isBetween(dayjs(this.hoveredDate), dayjs(this.selectedMonths[this.selectedMonths.length - 1]), 'month', '[]') && !this.isMonthRange();
        this.hoveredRangeEnd = this.isMonthRangeEnd() && dayjs(this.date).isBetween(dayjs(this.hoveredDate), dayjs(this.selectedMonths[this.selectedMonths.length - 1]), 'month', '[]') && !this.isMonthRange();
    }

    isRangeStart() {
        if (this.isMonthHovered()) {
            return dayjs(this.date).isSame(dayjs(this.date).startOf('month'), 'day');
        }
        return (this.isSame(this.startDate) && (!this.hovered && DateUtils.isAfter(this.hoveredDate, this.startDate)) || !!this.endDate)
            || (this.selected && this.hovered);
    }

    isRange() {
        return this.isHovered() || this.isInside() || this.isMonthHovered();
    }

    isMonthRange() {
        return this.isInsideSelectedMonths()
    }

    isMonthRangeStart(date?: Date) {
        return dayjs(date ?? this.date).isSame(dayjs(date ?? this.date).startOf('month'), 'day');
    }

    isMonthRangeEnd() {
        if (this.disabled || dayjs(this.date).isAfter(dayjs(), 'day')) {
            return false;
        }

        const currentDate = dayjs(this.date);
        const endOfMonth = currentDate.endOf('month');
        const nextDay = currentDate.add(1, 'day');
        const today = dayjs().startOf('day');

        if (currentDate.isSame(endOfMonth, 'day')) {
            return true;
        }

        if (nextDay.isAfter(endOfMonth, 'day')) {
            return true;
        }

        return nextDay.isAfter(today, 'day');

    }

    isMonthHovered() {
        return !this.startDate && !this.endDate && this.hoveredMonth
            && dayjs(this.date).isSame(dayjs(this.hoveredMonth), 'month')
            && !this.disabled;
    }

    isHovered() {
        return this.startDate && !this.endDate && this.hoveredDate
            && (this.isAfter(this.startDate) || DateUtils.isSame(this.date, this.startDate)) && (this.isBefore(this.hoveredDate) || this.hovered);
    }

    isInside() {
        return this.endDate && (DateUtils.isAfter(this.date, this.startDate)
            || this.isSame(this.startDate)) && (this.isBefore(this.endDate)
            || this.isSame(this.endDate));
    }

    isInsideMonth(date: Date) {
        return dayjs(date).isSame(dayjs(this.date), 'month');
    }

    isInsideSelectedMonths() {
        const currentMonth = dayjs(this.date).toDate();

        return this.selectedMonths.some(month => dayjs(month).isSame(dayjs(currentMonth), 'month')) && !this.disabled;
    }

    isRangeEnd() {
        if (this.isMonthHovered()) {
            return this.isMonthRangeEnd();
        }
        return this.isHovered() && this.isSame(this.hoveredDate) || this.isSame(this.endDate);
    }

    isStatic() {
        return false;
    }

    isInactive() {
        return false;
    }

    isSuggested() {
        return false;
    }

    // helpers
    isAfter(date: Date): boolean {
        return DateUtils.isAfter(this.date, date);
    }

    isBefore(date: Date): boolean {
        return DateUtils.isBefore(this.date, date);
    }

    isSame(date: Date): boolean {
        return DateUtils.isSame(this.date, date);
    }

    isSameMonth(date: Date): boolean {
        return dayjs(date).isSame(dayjs(this.date), 'month');
    }
}
